/**
 * ─────────────────────────────────────────────────────────────────────────────
 * XAVIER: the domain and the contact address are set. `legalEntity` and
 * `effectiveDate` are still yours to fill in — both are legal text.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every value below appears in the public marketing pages, the terms of service
 * and the privacy policy. Nothing is inlined into page copy, so changing a value
 * here changes it everywhere — including the URLs Google's OAuth consent screen
 * has to match.
 *
 * The placeholders are deliberately obvious. `PLACEHOLDER` in rendered output
 * means this file has not been filled in yet.
 *
 * Note from the cloud-storage research spike: the production origin **cannot**
 * be the Tailscale address. Google requires ownership verification of the
 * callback domain and `ts.net` belongs to Tailscale, the same reason it blocks
 * tunnel hosts like `ngrok.io`.
 */

export const SITE = {
  /**
   * Product name, as shown to users.
   *
   * Named for Aldus Manutius, the Venetian printer who around 1501 invented the
   * octavo — the small, well-set book you could carry with you. The repo,
   * packages, containers and internal paths deliberately still say
   * `bmad-kindle`; only user-visible surfaces carry this.
   */
  name: 'Aldus',

  /**
   * One-line positioning statement. Meta description and Open Graph only — the
   * landing page's own copy comes from `strings.marketing.*`, transcribed from
   * the copy deck. This is HERO-2, so the two agree.
   */
  // Deliberately NOT a catalogue key: this is the copy deck's HERO-2 line and
  // the string Google's OAuth consent screen has to match, so it is one English
  // constant with one owner. Its localized twin is
  // `marketing.meta.description`, and `marketing.test.tsx` asserts the two are
  // the same sentence.
  tagline:
    // eslint-disable-next-line no-restricted-syntax -- see above
    'Aldus helps you manage your e-book library and sync with your e-reader, while giving you full control over your files.',

  /**
   * Production origin, no trailing slash. Used for `metadataBase` and therefore
   * for every absolute Open Graph URL.
   *
   * Decided 2026-08-25. This exact origin must also be entered as an authorized
   * JavaScript origin in the Google Cloud console, and it is the origin the OPDS
   * feed URLs in `public/marketing/opds.jpg` are captured at — re-shoot that
   * screenshot if this ever changes, or the page shows a domain that is not ours.
   */
  url: 'https://aldusapp.com',

  /**
   * Who the contract in the terms of service is with, and who acts as data
   * controller in the privacy policy.
   *
   * TODO(xavier): a legal name — your own name is fine for a sole operator.
   */
  legalEntity: 'PLACEHOLDER — legal entity or operator name',

  /**
   * Where users write about their data, their account, or these documents. Also
   * the support email on the OAuth consent screen.
   *
   * Decided 2026-08-25. The landing page footer renders this as the `href` of
   * "Contact us." rather than printing it; the legal pages print it in full,
   * because a policy that tells you to write somewhere has to say where.
   */
  contactEmail: 'contact@aldusapp.com',

  /**
   * Shown at the top of the terms and privacy pages.
   *
   * TODO(xavier): set to the date you publish, after reviewing both drafts.
   */
  effectiveDate: 'PLACEHOLDER — not yet published',
} as const

/** How long a removed book stays recoverable before it is purged. */
export const TRASH_RETENTION_DAYS = 30
